/**
 * Overlap products popover for Revenue Stats (WSRADM-0036).
 *
 * Renders overlapProducts / overlapLineCount / truncated as sent — never sums
 * or compares them. Family chips resolve against familyColumns only.
 */
import {
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMonthLabel } from '@/api/reports/format';
import type { FamilyColumn, RevenueOverlapProduct } from '@/api/reports/types';
import { familyColorOrNeutral } from '@/components/familyPalette';
import { useCopy } from '@/hooks/useCopy';

const HEADING_ID = 'overlap-popover-heading';

export interface OverlapPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  month: string; // yyyy-MM, for the heading
  overlapLineCount: number;
  products: readonly RevenueOverlapProduct[];
  truncated: number;
  familyColumns: readonly FamilyColumn[];
  dimension: string | null; // slug; null → no "Fix allocation" link
}

function FamilySlugChip({
  slug,
  familyColumns,
}: {
  slug: string;
  familyColumns: readonly FamilyColumn[];
}) {
  const column = familyColumns.find((c) => c.slug === slug);
  const label = column?.displayName ?? slug;
  const resolved = familyColorOrNeutral(column?.color ?? null);
  return (
    <Chip
      size="small"
      label={label}
      variant={resolved === null ? 'outlined' : 'filled'}
      sx={resolved !== null ? { bgcolor: resolved.bg, color: resolved.fg } : undefined}
    />
  );
}

export function OverlapPopover({
  open,
  anchorEl,
  onClose,
  month,
  overlapLineCount,
  products,
  truncated,
  familyColumns,
  dimension,
}: OverlapPopoverProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const monthLabel = formatMonthLabel(month, i18n.language);
  const linesLabel = t(
    'reports.revenue.overlapLines',
    '{{n}} lines counted in more than one family',
    { n: overlapLineCount },
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': HEADING_ID,
          sx: { maxWidth: 420, p: 2 },
        },
      }}
    >
      <Typography id={HEADING_ID} variant="subtitle1" component="h2" gutterBottom>
        {monthLabel} — {linesLabel}
      </Typography>

      <List dense disablePadding>
        {products.map((product) => {
          const primaryName =
            product.productName !== null
              ? product.productName
              : String(product.sourceProductId);
          return (
            <ListItem key={product.sourceProductId} alignItems="flex-start" disableGutters>
              <ListItemText
                primary={
                  <>
                    {primaryName}
                    {product.productName !== null ? (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        #{product.sourceProductId}
                      </Typography>
                    ) : null}
                  </>
                }
                secondary={
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                      alignItems: 'center',
                      mt: 0.5,
                    }}
                  >
                    {product.familySlugs.map((slug) => (
                      <FamilySlugChip
                        key={`${product.sourceProductId}-${slug}`}
                        slug={slug}
                        familyColumns={familyColumns}
                      />
                    ))}
                    <Typography variant="caption" component="span" color="text.secondary">
                      {t('reports.revenue.overlapLineCountOf', '{{n}} lines', {
                        n: product.lineCount,
                      })}
                    </Typography>
                  </Box>
                }
                slotProps={{ secondary: { component: 'div' } }}
              />
            </ListItem>
          );
        })}
      </List>

      {truncated > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('reports.revenue.overlapMore', '+{{n}} more products', { n: truncated })}
        </Typography>
      ) : null}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        {t(
          'reports.revenue.overlapHelp',
          'Products in more than one family of this dimension are counted in each. Remove the extra family on the board and Save and build; this count updates on the next rebuild.',
        )}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        {dimension !== null ? (
          <Button
            component={RouterLink}
            to={`/configuration?dimension=${encodeURIComponent(dimension)}`}
            size="small"
            variant="outlined"
          >
            {t('reports.revenue.overlapFix', 'Fix allocation')}
          </Button>
        ) : null}
        <Button size="small" onClick={onClose}>
          {t('reports.revenue.overlapClose', 'Close')}
        </Button>
      </Box>
    </Popover>
  );
}
